import webSocket from "@app/config/webSocket";
import type { MessageOfAction, WebsocketMessage } from "@globals";
import { useEffect, useRef } from "react";

/**
 * Attaches a callback to a websocket event namespace for the lifetime of the component.
 * Safely handles the callback changing without re-attaching the listener.
 */
export function useWebSocket<TAction extends WebsocketMessage["action"]>(
  action: TAction,
  callback: (message: MessageOfAction<TAction>) => void,
  filter?: (message: MessageOfAction<TAction>) => boolean
) {
  // Use a ref to hold the callback. This ensures the listener always
  // calls the LATEST version of the callback without needing to be re-attached.
  const callbackRef = useRef(callback);
  useEffect(() => {
    callbackRef.current = callback;
  });

  useEffect(() => {
    // The listener itself never changes.
    const listener = ((event: CustomEvent) => {
      const message = event.detail as MessageOfAction<TAction>;

      // If a filter is provided, skip messages that don't pass it.
      if (typeof filter === "function" && !filter(message)) {
        return;
      }
      callbackRef.current(message);
    }) as EventListener;

    webSocket.addEventListener(action, listener);

    return () => {
      webSocket.removeEventListener(action, listener);
    };
  }, [action, filter]);
}
